import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import { makeStyles } from '@mui/styles'
import * as Yup from 'yup'
import PropTypes from 'prop-types'
import config from '@/config'
import lang from '@/lang'

import { TextInput, SelectInput } from '@/modules/base/components/inputs'
import PageWrapper from '@/modules/base/components/modalWizard/pageWrapper'
import { UserSelect } from '@/modules/user/components/user-select/user-select'
import useCreateUser from './context'

import Radio from '@/modules/client/components/list/radio'
import RadioContent from '@/modules/client/components/list/radio/content'
import Finish from './finish'
import Loader from '@/modules/base/components/loader/loader-inline'

import {
  COMPANY_FORM_AG,
  COMPANY_FORM_EK,
  COMPANY_FORM_FREELANCER,
  COMPANY_FORM_GBR,
  COMPANY_FORM_GMBH,
  COMPANY_FORM_GMBH_COKG,
  COMPANY_FORM_KG,
  COMPANY_FORM_OHG,
  COMPANY_FORM_OTHER_COMPANY,
  COMPANY_FORM_OTHER_PARTNERSHIP,
  COMPANY_FORM_PERSON,
  COMPANY_FORM_SILENT,
  COMPANY_FORM_SOLE_TRADER,
  COMPANY_FORM_TRADER,
  COMPANY_FORM_UG,
  COMPANY_FORM_LEGAL_FOUNDATION,
  COMPANY_FORM_CHARITABLE_FOUNDATION,
  CONNECTION_TYPE_INVITATION,
} from '@/modules/client/constants'
import request from '@/modules/base/util/request'

const useStyles = makeStyles(() => ({
  userSelect: {
    '& .react-select__menu': {
      zIndex: 2147483647,
    },
  },
  form: {
    width: '100%',
    flexGrow: 1,
    '& label:not(:first-child)': {
      marginTop: 20,
    },
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    gap: 20,
    '& + $row': {
      marginTop: 20,
    },
  },
}))

const {
  companyFormGroups,
  companyForm,
} = lang.createClient

export const getCompanyFormGroupLabelByCompanyForm = (value) => {
  for (const option of companyFormOptions) {
    const matchingOption = option.options.find((opt) => opt.value === value)
    if (matchingOption) return matchingOption.label
  }
}

export const companyFormOptions = [
  {
    label: companyFormGroups.person,
    options: [{ value: COMPANY_FORM_PERSON, label: companyForm.person }],
  },
  {
    label: companyFormGroups.onemanbusiness,
    options: [
      { value: COMPANY_FORM_EK, label: companyForm.ek },
      { value: COMPANY_FORM_TRADER, label: companyForm.trader },
      { value: COMPANY_FORM_FREELANCER, label: companyForm.freelancer },
      { value: COMPANY_FORM_SOLE_TRADER, label: companyForm.sole_trader },
    ],
  },
  {
    label: companyFormGroups.partnerships,
    options: [
      { value: COMPANY_FORM_GBR, label: companyForm.gbr },
      { value: COMPANY_FORM_OHG, label: companyForm.ohg },
      { value: COMPANY_FORM_KG, label: companyForm.kg },
      { value: COMPANY_FORM_GMBH_COKG, label: companyForm.gmbh_cokg },
      { value: COMPANY_FORM_SILENT, label: companyForm.silent },
      { value: COMPANY_FORM_LEGAL_FOUNDATION, label: companyForm.legalFoundation },
      { value: COMPANY_FORM_CHARITABLE_FOUNDATION, label: companyForm.charitableFoundation },
      { value: COMPANY_FORM_OTHER_PARTNERSHIP, label: companyForm.other_partnership },
    ],
  },
  {
    label: companyFormGroups.corporations,
    options: [
      { value: COMPANY_FORM_UG, label: companyForm.ug },
      { value: COMPANY_FORM_GMBH, label: companyForm.gmbh },
      { value: COMPANY_FORM_AG, label: companyForm.ag },
      { value: COMPANY_FORM_OTHER_COMPANY, label: companyForm.other_company },
    ],
  },
]

const CompanyForm = ({ setFieldValue, values }) => {
  const classes = useStyles()

  return (
    <>
      <div className={classes.row}>
        <SelectInput
          label={lang.createClient.companyFormFields.companyForm}
          name="companyForm"
          useFormik
          notAsync
          options={companyFormOptions}
        />
      </div>
      <div className={classes.row}>
        <TextInput
          label={lang.createClient.companyFormFields.companyName}
          name="companyName"
          useFormik
        />
      </div>
      <div className={classes.row}>
        <TextInput
          label={lang.createClient.companyFormFields.clientNumber}
          name="clientNumber"
          useFormik
        />
        <UserSelect
          className={classes.userSelect}
          label={lang.createClient.companyFormFields.responsibleUser}
          customSelectOptions={{
            isMulti: true,
          }}
          onChange={value => setFieldValue('clerkIds', value)}
          value={values?.clerkIds}
        />
      </div>
    </>
  )
}

const CompanySelection = ({ name, selection, handleChange, tenants }) => {
  const { client } = useCreateUser()

  return tenants.map(tenant => {
    const { isConnected, address: { companyName, street, zipCode, city } } = tenant
    return (
      <Radio key={`key_${tenant.id}`}
        name={name}
        value={tenant.id}
        checked={selection === tenant.id}
        onChange={handleChange}
        disabled={isConnected}
      >
        <RadioContent
          title={companyName || client.email}
          subtitle={(
            <>
              {street && (<span>{street}</span>)}
              {(zipCode || city) && (
                <>
                  <br />
                  <span>{`${zipCode}${`${city ? ' ' : ''}${city}`}`}</span>
                </>
              )}
            </>
          )}
        />
      </Radio>
    )
  })
}

CompanySelection.propTypes = {
  name: PropTypes.string.isRequired,
  selection: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  tenants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      address: PropTypes.shape({
        companyName: PropTypes.string,
        street: PropTypes.string,
        zipCode: PropTypes.string,
        city: PropTypes.string,
      }),
    }).isRequired,
  ).isRequired,
}

CompanySelection.defaultProps = {
  name: '',
  selection: '',
  handleChange: () => {},
  tenants: [],
}

const NewClientRadio = ({ checked, handleChange }) =>
  (<Radio key={'new'}
    name={name}
    value={null}
    checked={checked}
    onChange={handleChange}
    disabled={false}
  >
    <RadioContent
      title={lang.createClient.company.createNewTenant}
    />
  </Radio>)

const Company = ({ setTenantId, setConnectionType, tenantId, setShouldRenderFinish }) => {
  const { client, patch, submit, loading } = useCreateUser()
  const [tenants, setTenants] = useState([])
  const [submitted, setSubmitted] = useState(false)
  const classes = useStyles()
  const allTenantsConnected = tenants.length > 0 ? tenants?.every(tenant => tenant.isConnected) : false
  const tenantSelected = tenants.length > 0 && !tenantId && !submitted

  useEffect(() => {
    const fetch = async() => {
      const result = await request(`${config.resourceHost}tenant/checkTenantCollaborationStatus?email=${encodeURIComponent(client.email)}`, {
        method: 'GET',
        auth: true,
        isConcurrent: true,
      })
      setTenants(result.body.data)
    }
    if (client.email) fetch()
    else setTenants([])
  }, [client.email])

  const companyFormValidation = {
    companyForm: Yup.string().required(lang.emptyRequiredFieldErrorMessage),
    companyName: Yup.string().required(lang.emptyRequiredFieldErrorMessage),
    clientNumber: Yup.string(),
    clerkIds: Yup.array().required(lang.emptyRequiredFieldErrorMessage),
  }
  const companySelectValidation = {
    tenantId: Yup.string().required(lang.emptyRequiredFieldErrorMessage),
  }
  const shouldRenderSelection = tenants.length > 0 && !tenantId
  const validationSchema = Yup.object(shouldRenderSelection ? companySelectValidation : companyFormValidation)

  const titleText = tenants.length ? lang.createClient.company.tenantPresentTitle : lang.createClient.company.title
  const subtitleText = tenants.length ? lang.createClient.company.tenantPresentText(client?.email) : lang.createClient.company.text

  const onNewClientClicked = (setFieldValue) => {
    setFieldValue('tenantId', '')
    setFieldValue('connectionType', CONNECTION_TYPE_INVITATION)
    setTenantId(null)
    setTenants([])
  }

  return Boolean(tenants) && (
    <Formik
      initialValues={client}
      onSubmit={async values => {
        patch(values)
        try {
          const { id: tenantId } = await submit()
          setTenantId(tenantId)
          setConnectionType(values?.connectionType)
          setSubmitted(true)
          setShouldRenderFinish(true)
        } catch {}
      }}
      enableReinitialize
      validationSchema={validationSchema}
      validateOnMount
    >
      {({ handleSubmit, isValid, values, handleChange, setFieldValue }) => {
        return (
          <PageWrapper
            heading={lang.createClient.company.heading}
            title={titleText}
            text={subtitleText}
            disabled={!isValid || allTenantsConnected}
            onNextButtonClick={() => {
              if (tenantSelected) {
                setTenantId(values.tenantId)
                return false
              } else {
                handleSubmit()
                return false
              }
            }}
            onBackButtonClick={() => {
              patch(values)
              return true
            }}
          >
            {loading
              ? (
                <Form className={classes.form}>
                  <Loader />
                </Form>
              )
              : (
                <Form className={classes.form}>
                  {shouldRenderSelection
                    ? (<>
                      <CompanySelection name="tenantId" tenants={tenants} selection={values.tenantId} handleChange={handleChange} />
                      <NewClientRadio checked={tenantId === null} name="tenantId" handleChange={() => onNewClientClicked(setFieldValue)} />
                    </>)
                    : <CompanyForm setFieldValue={setFieldValue} values={values} />
                  }
                </Form>
              )}
          </PageWrapper>
        )
      }

      }
    </Formik>
  )
}

Company.propTypes = {
  setTenantId: PropTypes.func.isRequired,
  setConnectionType: PropTypes.func.isRequired,
  clientData: PropTypes.object,
}

Company.defaultProps = {
  setTenantId: () => {},
  setConnectionType: () => {},
  clientData: {},
}

const CompanyData = ({ onClose, clientData }) => {
  const [tenantId, setTenantId] = useState()
  const [connectionType, setConnectionType] = useState()
  const [shouldRenderFinish, setShouldRenderFinish] = useState(false)

  return tenantId && shouldRenderFinish
    ? <Finish connectionType={connectionType} tenantId={tenantId} onClose={onClose} />
    : <Company
      setTenantId={setTenantId}
      setConnectionType={setConnectionType}
      tenantId={tenantId}
      clientData={clientData}
      setShouldRenderFinish={setShouldRenderFinish}
    />
}

export default CompanyData
